/**
 * ZIP central-directory preflight for FenzSafe discovery's Office-container formats (DOCX today;
 * see docs/ai/FENZSAFE_DISCOVERY_FEASIBILITY_CONTRACT.md §13, approved and refined by Issue #114
 * founder review comment 5730113935).
 *
 * DOCX (and XLSX, still deferred) are ZIP containers. A ZIP's own central directory -- a section
 * at the *end* of the file, located via the End Of Central Directory (EOCD) record's fixed
 * signature -- lists every entry's *declared* compressed/uncompressed size as plain binary
 * integers, without decompressing anything. Reading only this section lets a small, cheap check
 * catch a zip-bomb-style archive (a tiny file that would expand to gigabytes) before a real parser
 * (`mammoth`) ever touches it, and before any of its compressed entry data is read at all.
 *
 * Per the founder review: this is a **rejection layer, not proof that decompression is safe** --
 * a file passing preflight still goes through its own bounded, timed-out, per-file-isolated parse
 * (see fenzsafeDiscovery.ts's docxContentReasons()). ZIP64 (the 64-bit extension for archives
 * whose classic 32-bit fields would overflow) is explicitly NOT supported -- detected and
 * rejected fail-closed, never partially interpreted, since this codebase has not implemented or
 * tested correct ZIP64 field handling. Every offset/length this module reads from file bytes is
 * bounds-checked against the buffer's own length before being trusted for the next read.
 */

const EOCD_SIGNATURE = 0x06054b50;
const EOCD_FIXED_SIZE = 22;
const EOCD_MAX_COMMENT_SIZE = 65535;

const CENTRAL_DIRECTORY_ENTRY_SIGNATURE = 0x02014b50;
const CENTRAL_DIRECTORY_ENTRY_FIXED_SIZE = 46;

/** A 32-bit field holding this exact value is the ZIP64 format's own signal that the real value lives in a 64-bit extra field instead -- never a legitimate 32-bit value in a real (non-ZIP64) archive. */
const ZIP64_SENTINEL_32 = 0xffffffff;
const ZIP64_SENTINEL_16 = 0xffff;

/** Conservative MVP limits (Issue #114 review comment 5730113935) -- not measured against a corpus of real DOCX files, and expected to be tuned from real benign-file evidence later. Named and exported so callers/tests reference the same constants this module enforces, never a copied literal. */
export const DOCX_PREFLIGHT_MAX_ENTRY_UNCOMPRESSED_BYTES = 50_000_000;
export const DOCX_PREFLIGHT_MAX_TOTAL_UNCOMPRESSED_BYTES = 150_000_000;
export const DOCX_PREFLIGHT_MAX_ENTRY_COUNT = 5000;
/** A real DOCX's XML parts (highly repetitive, but not adversarial) typically compress at well under 20:1; pathological zip bombs typically reach ratios in the thousands-to-one range or higher. 200:1 is comfortably above the former and well below the latter. */
export const DOCX_PREFLIGHT_MAX_COMPRESSION_RATIO = 200;

export type ArchivePreflightResult =
  | {ok: true; entryCount: number; totalDeclaredUncompressedBytes: number}
  | {ok: false; reason: string};

function reject(reason: string): ArchivePreflightResult {
  return {ok: false, reason};
}

/**
 * Scans backward from the end of the buffer for the EOCD signature -- the standard, safe way to
 * locate it, since the only thing that can follow it is a comment field of known maximum length.
 * Verifies the candidate's own comment-length field is consistent with the buffer's actual length
 * before accepting it, so a signature-looking byte sequence embedded earlier in a crafted comment
 * can't be mistaken for the real EOCD (scanning from the end and checking consistency both matter
 * here, not just finding *a* match).
 */
function findEndOfCentralDirectory(view: DataView): {centralDirectoryOffset: number; centralDirectorySize: number; totalEntries: number} | null {
  const fileLength = view.byteLength;
  if (fileLength < EOCD_FIXED_SIZE) {
    return null;
  }
  const searchFloor = Math.max(0, fileLength - EOCD_FIXED_SIZE - EOCD_MAX_COMMENT_SIZE);
  for (let position = fileLength - EOCD_FIXED_SIZE; position >= searchFloor; position -= 1) {
    if (view.getUint32(position, true) !== EOCD_SIGNATURE) {
      continue;
    }
    const commentLength = view.getUint16(position + 20, true);
    if (position + EOCD_FIXED_SIZE + commentLength !== fileLength) {
      continue;
    }
    return {
      totalEntries: view.getUint16(position + 10, true),
      centralDirectorySize: view.getUint32(position + 12, true),
      centralDirectoryOffset: view.getUint32(position + 16, true),
    };
  }
  return null;
}

/**
 * Reads only the ZIP central directory (never any entry's actual compressed data, never a local
 * file header) and checks it against the discovery-safety limits above. Any structural anomaly
 * -- a missing/inconsistent EOCD, an out-of-bounds offset, a truncated entry, a ZIP64 sentinel --
 * is a rejection, never a thrown error and never a partial/best-effort read.
 */
export function preflightZipArchive(buffer: ArrayBuffer): ArchivePreflightResult {
  let view: DataView;
  try {
    view = new DataView(buffer);
  } catch {
    return reject('not readable as binary data');
  }

  const eocd = findEndOfCentralDirectory(view);
  if (!eocd) {
    return reject('no End Of Central Directory record found');
  }
  const {centralDirectoryOffset, centralDirectorySize, totalEntries} = eocd;

  if (centralDirectorySize === ZIP64_SENTINEL_32 || centralDirectoryOffset === ZIP64_SENTINEL_32 || totalEntries === ZIP64_SENTINEL_16) {
    return reject('ZIP64 archives are not supported');
  }

  const fileLength = view.byteLength;
  if (centralDirectoryOffset < 0 || centralDirectoryOffset > fileLength || centralDirectorySize < 0 || centralDirectoryOffset + centralDirectorySize > fileLength) {
    return reject('central directory offset/size is out of bounds for this file');
  }

  const centralDirectoryEnd = centralDirectoryOffset + centralDirectorySize;
  let cursor = centralDirectoryOffset;
  let entryCount = 0;
  let totalDeclaredUncompressedBytes = 0;

  while (cursor < centralDirectoryEnd) {
    if (cursor + CENTRAL_DIRECTORY_ENTRY_FIXED_SIZE > centralDirectoryEnd) {
      return reject('a central directory entry header is truncated');
    }
    if (view.getUint32(cursor, true) !== CENTRAL_DIRECTORY_ENTRY_SIGNATURE) {
      return reject('a central directory entry has an invalid signature');
    }

    const compressedSize = view.getUint32(cursor + 20, true);
    const uncompressedSize = view.getUint32(cursor + 24, true);
    if (compressedSize === ZIP64_SENTINEL_32 || uncompressedSize === ZIP64_SENTINEL_32) {
      return reject('ZIP64 archives are not supported');
    }

    const fileNameLength = view.getUint16(cursor + 28, true);
    const extraFieldLength = view.getUint16(cursor + 30, true);
    const fileCommentLength = view.getUint16(cursor + 32, true);
    const entryTotalSize = CENTRAL_DIRECTORY_ENTRY_FIXED_SIZE + fileNameLength + extraFieldLength + fileCommentLength;
    if (cursor + entryTotalSize > centralDirectoryEnd) {
      return reject('a central directory entry extends past the central directory');
    }

    entryCount += 1;
    if (entryCount > DOCX_PREFLIGHT_MAX_ENTRY_COUNT) {
      return reject(`archive has more than ${DOCX_PREFLIGHT_MAX_ENTRY_COUNT} entries`);
    }

    if (uncompressedSize > DOCX_PREFLIGHT_MAX_ENTRY_UNCOMPRESSED_BYTES) {
      return reject('a single entry declares an uncompressed size over the per-entry limit');
    }

    // A zero compressed size paired with a non-trivial declared uncompressed size cannot occur in
    // a well-formed archive (even an uncompressed/"stored" entry has compressedSize ===
    // uncompressedSize) and is treated the same as an extreme compression ratio: a rejection, not
    // a divide-by-zero.
    if (uncompressedSize > 0 && (compressedSize === 0 || uncompressedSize / compressedSize > DOCX_PREFLIGHT_MAX_COMPRESSION_RATIO)) {
      return reject(`an entry's declared compression ratio exceeds ${DOCX_PREFLIGHT_MAX_COMPRESSION_RATIO}:1`);
    }

    totalDeclaredUncompressedBytes += uncompressedSize;
    if (totalDeclaredUncompressedBytes > DOCX_PREFLIGHT_MAX_TOTAL_UNCOMPRESSED_BYTES) {
      return reject('archive declares more total uncompressed bytes than the aggregate limit');
    }

    cursor += entryTotalSize;
  }

  return {ok: true, entryCount, totalDeclaredUncompressedBytes};
}
